/* eslint-disable no-nested-ternary */
import { createSlice } from '@reduxjs/toolkit';
import { message as toaster } from 'antd';
import { API } from 'api';
import { uploadFileToS3AndGetKey } from 'store/commonSlice';

const initialState = {};

export const vendorBillSliceV2 = createSlice({
	name: 'vendorBillStateV2',
	initialState,
	reducers: {},
});

// Helper function to clean filter_by object (remove undefined/null/empty values)
const cleanFilters = (filter_by) => {
	const clean = {};
	if (!filter_by) return clean;
	Object.keys(filter_by).forEach((key) => {
		const value = filter_by[key];
		if (value !== undefined && value !== null && value !== '') {
			clean[key] = value;
		}
	});
	return clean;
};

/**
 * 1. Create Vendor Bill V2
 * POST /api/v2/create  { create_type: 'vendor_bill', data: {...} }
 */
export const createVendorBillV2 =
	({ req_data, setErrors }) =>
	async () => {
		const data = {
			create_type: 'vendor_bill',
			data: req_data,
		};

		const CREDENTIALS = {
			url: '/create',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res?.status === 'success') {
				toaster.success(res.message || 'Vendor bill created successfully');
			}
			return res;
		});
	};

/**
 * 2. Update Vendor Bill V2 (Mark as Paid / Adjust Charges / Generic Update)
 * POST /api/v2/update  { update_type: 'vendor_bill', data: { id, ...fields } }
 */
const VENDOR_BILL_ID_REQUIRED_MSG = 'Vendor bill ID is required for update';

export const updateVendorBillV2 =
	({ req_data, setErrors }) =>
	async () => {
		if (!req_data || !req_data.id) {
			toaster.error(VENDOR_BILL_ID_REQUIRED_MSG);
			return Promise.reject(new Error(VENDOR_BILL_ID_REQUIRED_MSG));
		}

		const data = {
			update_type: 'vendor_bill',
			data: req_data,
		};

		const CREDENTIALS = {
			url: '/update',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res?.status === 'success') {
				toaster.success(res.message || 'Vendor bill updated successfully');
			}
			return res;
		});
	};

/**
 * 3. Update Vendor Bill Invoice Image (shared endpoint for GST and non-GST vendors)
 * POST /api/v2/update  { update_type: 'vendor_bill_gst_invoice_image', data: {...} }
 *
 */
export const updateVendorBillGstInvoiceImage =
	({
		vendor_bill_id,
		isGstRegistered,
		image_key,
		invoice_number,
		invoice_date,
		useV2API = true,
	}) =>
	async () => {
		if (!vendor_bill_id) {
			toaster.error('Vendor bill ID is required');
			return Promise.reject(new Error('Vendor bill ID is required'));
		}

		const payload = { vendor_bill_id };
		if (isGstRegistered) {
			payload.gst_invoice_image = image_key || null;
		} else {
			payload.non_gst_invoice_image = image_key || null;
		}
		if (invoice_number !== undefined) payload.vendor_invoice_number = invoice_number;
		if (invoice_date !== undefined) payload.vendor_invoice_date = invoice_date;

		const data = {
			update_type: 'vendor_bill_gst_invoice_image',
			data: payload,
		};

		const CREDENTIALS = {
			url: '/update',
			data,
			version: useV2API ? 'v2' : 'v1',
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res?.status === 'success') {
				toaster.success(res.message || 'GST invoice details saved successfully');
			} else if (res?.status === 'error') {
				const errorMsg = res?.error?.message || 'Failed to save GST invoice details';
				toaster.error(errorMsg);
			}
			return res;
		});
	};

/**
 * 4. Search Vendor Bills V2 (List Page)
 * POST /api/v2/search  { search_type: 'vendor_bill', filter_by: {...}, pagination: {...} }
 *
 * This is designed to work with RCTable dispatch pattern (offset/limit based).
 */
export const searchVendorBillsV2 =
	({
		search_type = 'vendor_bill',
		limit = 10,
		offset = 0,
		currentPage,
		filterflag = true,
		...rest
	}) =>
	async () => {
		const cleanFilterBy = cleanFilters(rest);

		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type,
				filter_by: cleanFilterBy,
				offset,
				limit,
				fetch_filters: filterflag,
				filter_list: {},
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			// Ensure rows is always an array - V2 API may return data in different formats
			const rawData = response?.data;
			const rows = Array.isArray(rawData)
				? rawData
				: Array.isArray(rawData?.results)
				? rawData.results
				: [];

			return {
				rows,
				count: response?.meta?.count || response?.pagination?.total || rawData?.total || 0,
				cities: response?.filter_list?.cities,
				clients: response?.filter_list?.clients,
				projects: response?.filter_list?.projects,
				vendors: response?.filter_list?.vendors,
				vehicle_type: response?.filter_list?.vehicle_type,
				trip_type: response?.filter_list?.trip_type,
			};
		});
	};

/**
 * 5. Get List of Invoice Trips V2 (for vendor bill generation form)
 * POST /api/v2/search  { search_type: 'vendor_bill_trip', filter_by: {...} }
 * Matches the V1 getListofInvoice from vendorBillSlice.
 */
export const getListofInvoiceV2 =
	({ search_type = 'vendor_bill_trip', currentPage, ...rest }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type,
				filter_by: rest?.data,
				limit: rest?.data?.limit || 1000,
				offset: rest?.data?.offset || 0,
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			const rawData = response?.data;
			const rows = Array.isArray(rawData)
				? rawData
				: Array.isArray(rawData?.results)
				? rawData.results
				: Array.isArray(rawData?.trips)
				? rawData.trips
				: [];

			const totalCount =
				response?.meta?.count ||
				response?.pagination?.total ||
				rawData?.pagination?.total ||
				rawData?.total ||
				rows.length;

			return {
				rows,
				count: totalCount,
			};
		});
	};

/**
 * 5b. Bulk Trip Fetch V2 — vendor bill generation.
 *
 * Separate from `getListofInvoiceV2` so errors reach the caller: the upload
 * modal shows file-level rejections inline and stays open for a retry.
 * V2 only — the V1 search ignores `bulk_trip_ids` entirely.
 *
 * @returns {Promise<{rows: Array, count: number, bulkFetch: object|null}>}
 */
export const bulkFetchVendorBillTripsV2 =
	({ filter_by = {}, entity, file }) =>
	async (dispatch) => {
		const key = await dispatch(uploadFileToS3AndGetKey({ entity, file }));

		const response = await API.common({
			url: '/search',
			data: {
				search_type: 'vendor_bill_trip',
				filter_by: cleanFilters({ ...filter_by, bulk_trip_ids: { entity, key } }),
			},
			version: 'v2',
			showLoading: true,
		});

		return {
			rows: response?.data?.trips || [],
			count: response?.data?.pagination?.total || 0,
			bulkFetch: response?.data?.bulk_fetch || null,
		};
	};

/**
 * 6. Get Vendor Bill Details V2
 * POST /api/v2/search  { search_type: 'vendor_bill_details', filter_by: { vendor_bill_id: <id> } }
 */
export const getVendorBillDetailsV2 =
	({ id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'vendor_bill_details',
				filter_by: {
					vendor_bill_id: id,
				},
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => response);
	};

/**
 * 7. Search Vendor Bill Trips V2
 * POST /api/v2/search  { search_type: 'vendor_bill_trip', filter_by: { vendor_bill_id: <id> }, pagination: {...} }
 *
 * Works with RCTable dispatch pattern.
 */
export const searchVendorBillTripsV2 =
	({ search_type = 'vendor_bill_trip', limit = 10, offset = 0, ...rest }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type,
				filter_by: { ...rest },
				limit,
				offset,
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			const rawData = response?.data;
			const rows = Array.isArray(rawData)
				? rawData
				: Array.isArray(rawData?.results)
				? rawData.results
				: Array.isArray(rawData?.trips)
				? rawData.trips
				: [];

			return {
				rows,
				count:
					response?.meta?.count ||
					response?.pagination?.total ||
					rawData?.pagination?.total ||
					rawData?.total ||
					0,
			};
		});
	};

/**
 * 8. List Search Vendor Bills V2 (Dropdown)
 * POST /api/v2/list_search  { list_search_type: 'vendor_bill', filter_by: {...}, limit: 50 }
 */
export const listSearchVendorBillsV2 =
	({ filter_by = {}, limit = 50 }) =>
	async () => {
		const cleanFilterBy = cleanFilters(filter_by);

		const CREDENTIALS = {
			url: '/list_search',
			data: {
				list_search_type: 'vendor_bill',
				filter_by: cleanFilterBy,
				limit: limit || 50,
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => ({
			results: response?.data?.results || [],
			total: response?.data?.total || 0,
		}));
	};

/**
 * 9. Regenerate Vendor Bill V2
 * POST /api/v2/update  { update_type: 'vendor_bill_regenerate', data: { vendor_bill_id, trip_ids, reason } }
 */
const VENDOR_BILL_ID_REQUIRED_FOR_REGENERATION_MSG = 'Vendor bill ID is required for regeneration';

export const regenerateVendorBillV2 =
	({ req_data, setErrors }) =>
	async () => {
		if (!req_data || !req_data.vendor_bill_id) {
			toaster.error(VENDOR_BILL_ID_REQUIRED_FOR_REGENERATION_MSG);
			return Promise.reject(new Error(VENDOR_BILL_ID_REQUIRED_FOR_REGENERATION_MSG));
		}

		const data = {
			update_type: 'vendor_bill_regenerate',
			data: req_data,
		};

		const CREDENTIALS = {
			url: '/update',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res?.status === 'success') {
				toaster.success(res.message || 'Vendor bill regeneration request created');
			}
			return res;
		});
	};

/**
 * Export Vendor Bill CSV V2
 * POST /api/v2/report  { report_type: 'vendor_bill_export', data: { format: 'csv', filter_by: {...} } }
 */
export const exportVendorBillCSVV2 =
	({ data }) =>
	async () => {
		const CREDENTIALS = {
			url: '/report',
			data,
			aclBlockApiCall: false,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res?.data?.download_url) {
				window.open(res.data.download_url);
			}
			return {
				rows: res?.data,
			};
		});
	};

/**
 * Export Vendor Bill PDF V2
 * POST /api/v2/report  { report_type: 'vendor_bill_export', data: { format: 'pdf', vendor_bill_id: <id> } }
 */
export const exportVendorBillPDFV2 =
	({ id }) =>
	async () => {
		const data = {
			report_type: 'vendor_bill_export',
			data: {
				format: 'pdf',
				vendor_bill_id: id,
			},
		};

		const CREDENTIALS = {
			url: '/report',
			data,
			aclBlockApiCall: false,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res?.data?.download_url) {
				window.open(res.data.download_url);
			}
			return res;
		});
	};

/**
 * Export Vendor Invoice Trips V2
 * POST /api/v2/report  (uses same pattern as V1 but with version: 'v2')
 */
export const exportVendorInvoiceTripsV2 =
	({ id }) =>
	async () => {
		const apiData = {
			report_type: 'vendor_invoice_trips_export',
			file_type: 'csv',
			data: {
				id,
				url_type: 'download',
			},
		};

		const CREDENTIALS = {
			url: '/report',
			data: apiData,
			aclBlockApiCall: false,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			if (response?.data?.download_url) {
				window.open(response.data.download_url);
			}
			return response;
		});
	};

/**
 * Get Vendor Bill Edit History V2
 * POST /api/v2/search  { search_type: 'vendor_bill_edit_history', filter_by: { vendor_bill_id: <id> } }
 */
export const getVendorBillEditHistoryV2 =
	({ id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'vendor_bill_edit_history',
				filter_by: {
					vendor_bill_id: id,
				},
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			const rawData = response?.data;
			const rows = Array.isArray(rawData)
				? rawData
				: Array.isArray(rawData?.results)
				? rawData.results
				: Array.isArray(rawData?.edit_history)
				? rawData.edit_history
				: [];

			return {
				data: rows,
				rows,
				meta: response?.meta || {},
			};
		});
	};

/**
 * Get Vendor Bill Payment History V2
 * POST /api/v2/search  { search_type: 'vendor_bill_payment_history', filter_by: { vendor_bill_id: <id> } }
 */
export const getVendorBillPaymentHistoryV2 =
	({ id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'vendor_bill_payment_history',
				filter_by: {
					vendor_bill_id: id,
				},
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			const rawData = response?.data;
			const rows = Array.isArray(rawData)
				? rawData
				: Array.isArray(rawData?.results)
				? rawData.results
				: Array.isArray(rawData?.payment_history)
				? rawData.payment_history
				: [];

			return {
				data: rows,
				rows,
				meta: response?.meta || {},
			};
		});
	};

// Helper function to validate vendor bill ID
const validateVendorBillId = (req_data) => {
	if (!req_data || !req_data.id) {
		toaster.error(VENDOR_BILL_ID_REQUIRED_MSG);
		return Promise.reject(new Error(VENDOR_BILL_ID_REQUIRED_MSG));
	}
	return null;
};

// Helper function to build update credentials
const buildUpdateCredentials = (updateType, req_data, setErrors) => ({
	url: '/update',
	data: {
		update_type: updateType,
		data: req_data,
	},
	setErrors,
	version: 'v2',
});

// Helper function to handle API response
const handleUpdateResponse = (res, successMessage) => {
	if (res?.status === 'success') {
		toaster.success(res.message || successMessage);
	}
	return res;
};

/**
 * Update Vendor Bill Charges V2
 * POST /api/v2/update  { update_type: 'vendor_bill_charges', data: { id, charges: {...}, tds_files: [...] } }
 */
export const updateVendorBillChargesV2 =
	({ req_data, setErrors }) =>
	async () => {
		const validationError = validateVendorBillId(req_data);
		if (validationError) {
			return validationError;
		}

		const CREDENTIALS = buildUpdateCredentials('vendor_bill_charges', req_data, setErrors);

		return API.common(CREDENTIALS).then((res) =>
			handleUpdateResponse(res, 'Vendor bill charges updated successfully')
		);
	};

export default vendorBillSliceV2.reducer;
